<section class="services-page" [attr.aria-busy]="loading">
  <header class="page-intro">
    <div>
      <p>Configured systems</p>
      <h2>Services</h2>
      <span>Monitor service health and open a sync source to manage its functions and execution history.</span>
    </div>
    <button type="button" class="refresh" [disabled]="loading" (click)="load()">
      <i class="pi pi-refresh" [class.pi-spin]="loading" aria-hidden="true"></i>
      Refresh
    </button>
  </header>

  <div class="messages" *ngIf="servicesError || healthError">
    <div class="message error" *ngIf="servicesError" role="alert">{{ servicesError }}</div>
    <div class="message warning" *ngIf="healthError" role="alert">{{ healthError }} Statuses are unavailable.</div>
  </div>

  <div class="service-grid" aria-label="Configured services">
    <article class="service-card" *ngFor="let card of cards" [class.unhealthy]="healthState(card) === 'unhealthy'">
      <div class="card-top">
        <div class="source-icon"><i [class]="card.icon" aria-hidden="true"></i></div>
        <span class="health-status" [class]="'health-status ' + healthState(card)">
          <i class="pi" [class.pi-check]="healthState(card) === 'healthy'"
            [class.pi-times]="healthState(card) === 'unhealthy'"
            [class.pi-question]="healthState(card) === 'unavailable'"
            [class.pi-spin]="healthState(card) === 'checking'"
            [class.pi-spinner]="healthState(card) === 'checking'" aria-hidden="true"></i>
          {{ healthLabel(card) }}
        </span>
      </div>

      <div class="identity">
        <h3>{{ card.label }}</h3>
        <p>{{ card.description }}</p>
      </div>

      <ng-container *ngIf="syncService(card) as service; else schedulerSummary">
        <p class="function-count">{{ service.enabledFunctions }} of {{ service.totalFunctions }} functions enabled</p>
        <div class="stats">
          <div><strong>{{ service.activeRunCount }}</strong><span>Active jobs</span></div>
          <div><strong>{{ service.importedUserCount }}</strong><span>User changes</span></div>
        </div>
        <div class="latest">
          <span>Latest activity</span>
          <strong>{{ service.latestActivityAtUtc ? (service.latestActivityAtUtc | date:'medium') : 'No runs recorded' }}</strong>
          <small>Latest run: {{ service.latestRunStatus || 'No runs' }}</small>
        </div>
      </ng-container>

      <ng-template #schedulerSummary>
        <div class="scheduler-summary" *ngIf="!card.source; else unavailableSync">
          <i class="pi pi-cog" aria-hidden="true"></i>
          <div><strong>Central scheduling</strong><span>Coordinates configured sync functions and administration operations.</span></div>
        </div>
        <ng-template #unavailableSync>
          <div class="scheduler-summary muted">
            <i class="pi pi-info-circle" aria-hidden="true"></i>
            <div><strong>{{ servicesLoading ? 'Loading service data…' : 'Service data unavailable' }}</strong><span>Health diagnostics remain available.</span></div>
          </div>
        </ng-template>
      </ng-template>

      <div class="checked-at">
        <span>Health checked</span>
        <strong>{{ healthCheckedAt(card) ? (healthCheckedAt(card) | date:'medium') : 'Not available' }}</strong>
      </div>

      <p class="card-health-error" *ngIf="healthRefreshError(card)" role="alert">
        <i class="pi pi-exclamation-circle" aria-hidden="true"></i> {{ healthRefreshError(card) }}
      </p>

      <div class="card-actions">
        <button type="button" class="secondary" [disabled]="healthLoading || isHealthRefreshing(card)"
          (click)="refreshHealth(card)" [attr.aria-label]="'Refresh health for ' + card.label">
          <i class="pi pi-refresh" [class.pi-spin]="isHealthRefreshing(card)" aria-hidden="true"></i>
          {{ isHealthRefreshing(card) ? 'Checking…' : 'Refresh health' }}
        </button>
        <button type="button" class="secondary" (click)="openHealthDetails(card)">
          <i class="pi pi-heart" aria-hidden="true"></i> Health details
        </button>
        <button type="button" class="secondary" (click)="openOutboundIpDialog(card)">
          <i class="pi pi-globe" aria-hidden="true"></i> Get outbound IP
        </button>
        <a *ngIf="card.source" class="open-link" [routerLink]="['/sync-services', card.source]">
          Open service <i class="pi pi-arrow-right" aria-hidden="true"></i>
        </a>
      </div>
    </article>
  </div>

  <p-dialog [(visible)]="healthDialogVisible" [modal]="true" [closable]="true"
    [closeOnEscape]="true" [dismissableMask]="false" [draggable]="false" [resizable]="false"
    [header]="healthDialogTitle" [style]="{ width: 'min(94vw, 46rem)' }"
    closeAriaLabel="Close health details dialog" (onHide)="closeHealthDialog()">
    <div class="dialog-content" [attr.aria-busy]="healthDetailsLoading">
      <div class="dialog-loading" *ngIf="healthDetailsLoading" role="status" aria-live="polite">
        <i class="pi pi-spin pi-spinner" aria-hidden="true"></i><span>Running dependency checks…</span>
      </div>

      <div class="dialog-error" *ngIf="!healthDetailsLoading && healthDetailsError" role="alert">
        <i class="pi pi-exclamation-triangle" aria-hidden="true"></i>
        <div><strong>Health details unavailable</strong><span>{{ healthDetailsError }}</span></div>
      </div>

      <ng-container *ngIf="!healthDetailsLoading && healthReport as report">
        <div class="detail-summary" [class.unhealthy]="!report.healthy">
          <i class="pi" [class.pi-check-circle]="report.healthy" [class.pi-exclamation-circle]="!report.healthy" aria-hidden="true"></i>
          <div><strong>{{ report.healthy ? 'All dependencies healthy' : 'Issues detected' }}</strong><span>Checked {{ report.checkedAtUtc | date:'medium' }}</span></div>
        </div>
        <div class="table-wrap">
          <table>
            <thead><tr><th>Dependency</th><th>Status</th><th>Issue</th></tr></thead>
            <tbody>
              <tr *ngFor="let check of report.checks">
                <td><strong>{{ dependencyLabel(check.name) }}</strong></td>
                <td><span class="dependency-status" [class.healthy]="check.healthy">{{ check.healthy ? 'Healthy' : 'Unhealthy' }}</span></td>
                <td class="issue">{{ check.issue || '—' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <button *ngIf="!report.healthy" type="button" class="reference" (click)="copyReference(report.referenceId)">
          <i class="pi pi-copy" aria-hidden="true"></i> Copy log reference
        </button>
        <p class="dialog-feedback" aria-live="polite">{{ referenceFeedback }}</p>
      </ng-container>
    </div>
    <ng-template #footer>
      <div class="dialog-actions">
        <p-button *ngIf="healthDetailsError" label="Try again" icon="pi pi-refresh"
          severity="secondary" [outlined]="true" (onClick)="retryHealthDetails()" />
        <p-button label="Close" (onClick)="closeHealthDialog()" />
      </div>
    </ng-template>
  </p-dialog>

  <p-dialog [(visible)]="outboundIpDialogVisible" [modal]="true" [closable]="true"
    [closeOnEscape]="true" [dismissableMask]="false" [draggable]="false" [resizable]="false"
    [header]="outboundIpDialogTitle" [style]="{ width: 'min(92vw, 32rem)' }"
    closeAriaLabel="Close outbound IP dialog" (onHide)="closeOutboundIpDialog()">
    <div class="dialog-content" [attr.aria-busy]="outboundIpLoading">
      <div class="dialog-loading" *ngIf="outboundIpLoading" role="status" aria-live="polite">
        <i class="pi pi-spin pi-spinner" aria-hidden="true"></i><span>Retrieving outbound IP…</span>
      </div>
      <div class="ip-result" *ngIf="!outboundIpLoading && outboundIpResult as result">
        <span>Observed public address</span><strong>{{ result.address }}</strong>
        <div><span>{{ result.addressFamily }}</span><span>Checked {{ result.checkedAtUtc | date:'medium' }}</span></div>
      </div>
      <div class="dialog-error" *ngIf="!outboundIpLoading && outboundIpError" role="alert">
        <i class="pi pi-exclamation-triangle" aria-hidden="true"></i>
        <div><strong>IP address unavailable</strong><span>{{ outboundIpError }}</span></div>
      </div>
      <p class="diagnostic-note">This address was observed from one live outbound request. Azure may use additional or changing outbound addresses.</p>
      <p class="dialog-feedback" aria-live="polite">{{ outboundIpFeedback }}</p>
    </div>
    <ng-template #footer>
      <div class="dialog-actions">
        <p-button *ngIf="outboundIpResult" label="Copy IP address" icon="pi pi-copy"
          severity="secondary" [outlined]="true" (onClick)="copyOutboundIp()" />
        <p-button *ngIf="outboundIpError" label="Try again" icon="pi pi-refresh"
          severity="secondary" [outlined]="true" (onClick)="retryOutboundIp()" />
        <p-button *ngIf="outboundIpReferenceId" label="Copy log reference" icon="pi pi-copy"
          severity="secondary" [outlined]="true" (onClick)="copyOutboundIpReference()" />
        <p-button label="Close" (onClick)="closeOutboundIpDialog()" />
      </div>
    </ng-template>
  </p-dialog>
</section>
